/**
 * Cognito Authentication Integration
 * 
 * Handles OAuth Authorization Code flow with PKCE:
 * - Redirects to Cognito login with PKCE challenge
 * - Exchanges authorization code for tokens
 * - Stores tokens in sessionStorage and localStorage
 * - Refreshes expired tokens using refresh token
 * - Cleans up tokens on logout
 * - Supports password changes with proper scopes
 */

import { generateCodeVerifier, generateCodeChallenge, storePKCEVerifier, retrievePKCEVerifier } from './pkce';

interface CognitoTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresAt: number;
}

interface CognitoConfig {
  domain: string;
  clientId: string;
  redirectUri: string;
  logoutUri: string;
}

const STORAGE_KEY = 'cognito_tokens';

/**
 * Get Cognito configuration from environment variables
 * Uses current domain for redirect URIs to support both app.blueclipboard.com and v2.blueclipboard.com
 */
function getCognitoConfig(): CognitoConfig {
  // Use current origin for redirect/logout URIs to support multiple domains
  const currentOrigin = window.location.origin + '/';
  
  return {
    domain: import.meta.env.VITE_COGNITO_DOMAIN,
    clientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '5stvd7j9jv8qf8pscmmmoraqea',
    redirectUri: currentOrigin,
    logoutUri: currentOrigin,
  };
}

/**
 * Redirect to Cognito login page with PKCE
 * Requirements: 1.2
 */
export async function redirectToLogin(): Promise<void> {
  const config = getCognitoConfig();
  
  // Generate PKCE code verifier and challenge
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  
  // Store verifier for later use
  storePKCEVerifier(codeVerifier);
  
  // Build authorization URL with PKCE
  const params = new URLSearchParams({
    client_id: config.clientId,
    response_type: 'code',
    scope: 'email openid phone profile aws.cognito.signin.user.admin',
    redirect_uri: config.redirectUri,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  });
  
  const loginUrl = `https://${config.domain}/oauth2/authorize?${params.toString()}`;
  
  console.log('[Auth] Redirecting to Cognito login with PKCE');
  window.location.href = loginUrl;
}

/**
 * Redirect to Cognito logout page
 * Requirements: 1.7
 */
export function redirectToLogout(): void {
  const config = getCognitoConfig();
  const logoutUrl = `https://${config.domain}/logout?client_id=${config.clientId}&logout_uri=${encodeURIComponent(config.logoutUri)}`;
  window.location.href = logoutUrl;
}

/**
 * Extract authorization code from URL query parameters
 * Requirements: 1.4
 * 
 * Cognito returns authorization code in URL query:
 * ?code=abc123...
 */
export function extractAuthCodeFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  
  console.log('[Auth] Extracting authorization code from URL:', code ? 'Code present' : 'No code');
  
  return code;
}

/**
 * Exchange authorization code for tokens using PKCE
 * Requirements: 1.4
 */
export async function exchangeCodeForTokens(code: string): Promise<CognitoTokens | null> {
  const config = getCognitoConfig();
  const codeVerifier = retrievePKCEVerifier();
  
  if (!codeVerifier) {
    console.error('[Auth] No PKCE verifier found in storage');
    return null;
  }
  
  try {
    console.log('[Auth] Exchanging authorization code for tokens');
    
    const params = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.clientId,
      code: code,
      redirect_uri: config.redirectUri,
      code_verifier: codeVerifier,
    });
    
    const response = await fetch(`https://${config.domain}/oauth2/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });
    
    if (!response.ok) {
      const error = await response.text();
      console.error('[Auth] Token exchange failed:', response.status, error);
      return null;
    }
    
    const data = await response.json();
    
    // Calculate expiration time
    const expiresInSeconds = data.expires_in || 3600;
    const expiresAt = Date.now() + expiresInSeconds * 1000;
    
    const tokens: CognitoTokens = {
      accessToken: data.access_token,
      idToken: data.id_token,
      refreshToken: data.refresh_token || '',
      expiresAt,
    };
    
    console.log('[Auth] Tokens obtained successfully, expires at:', new Date(expiresAt));
    
    return tokens;
  } catch (error) {
    console.error('[Auth] Token exchange error:', error);
    return null;
  }
}

/**
 * Store tokens in both sessionStorage and localStorage
 * Requirements: 1.4
 */
export function storeTokens(tokens: CognitoTokens): void {
  const tokenData = JSON.stringify(tokens);
  sessionStorage.setItem(STORAGE_KEY, tokenData);
  localStorage.setItem(STORAGE_KEY, tokenData);
  console.log('[Auth] Tokens stored in sessionStorage and localStorage');
}

/**
 * Retrieve tokens from storage (sessionStorage first, then localStorage)
 */
export function getStoredTokens(): CognitoTokens | null {
  // Try sessionStorage first
  let tokenData = sessionStorage.getItem(STORAGE_KEY);
  
  // Fall back to localStorage
  if (!tokenData) {
    tokenData = localStorage.getItem(STORAGE_KEY);
    
    // If found in localStorage, restore to sessionStorage
    if (tokenData) {
      sessionStorage.setItem(STORAGE_KEY, tokenData);
    }
  }

  if (!tokenData) {
    return null;
  }

  try {
    return JSON.parse(tokenData) as CognitoTokens;
  } catch (error) {
    console.error('Failed to parse stored tokens:', error);
    return null;
  }
}

/**
 * Clear all stored tokens and PKCE verifier
 * Requirements: 1.7
 */
export function clearTokens(): void {
  sessionStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem('pkce_verifier');
  console.log('[Auth] Tokens and PKCE verifier cleared from storage');
}

/**
 * Check if tokens are expired
 */
export function areTokensExpired(tokens: CognitoTokens): boolean {
  return Date.now() >= tokens.expiresAt;
}

/**
 * Refresh expired tokens using refresh token
 * Requirements: 1.5, 1.6
 */
export async function refreshTokens(tokens: CognitoTokens): Promise<CognitoTokens | null> {
  if (!tokens.refreshToken) {
    console.warn('[Auth] No refresh token available');
    return null;
  }

  const config = getCognitoConfig();

  try {
    console.log('[Auth] Refreshing tokens');
    
    const params = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: config.clientId,
      refresh_token: tokens.refreshToken,
    });
    
    const response = await fetch(`https://${config.domain}/oauth2/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });

    if (!response.ok) {
      console.error('[Auth] Token refresh failed:', response.status);
      return null;
    }

    const data = await response.json();
    
    const newTokens: CognitoTokens = {
      accessToken: data.access_token,
      idToken: data.id_token,
      refreshToken: tokens.refreshToken, // Refresh token stays the same
      expiresAt: Date.now() + data.expires_in * 1000,
    };

    console.log('[Auth] Tokens refreshed successfully');
    return newTokens;
  } catch (error) {
    console.error('[Auth] Token refresh error:', error);
    return null;
  }
}

/**
 * Decode JWT token to extract payload
 */
export function decodeJWT(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return null;
  }
}

/**
 * Extract user groups from ID token
 * Requirements: 1.9, 1.10, 1.11
 */
export function getUserGroups(idToken: string): string[] {
  const payload = decodeJWT(idToken);
  if (!payload) {
    return [];
  }

  // Cognito stores groups in 'cognito:groups' claim
  return payload['cognito:groups'] || [];
}

/**
 * Check if user is admin
 */
export function isAdmin(groups: string[]): boolean {
  return groups.includes('admin');
}

/**
 * Check if user is coach
 */
export function isCoach(groups: string[]): boolean {
  return groups.includes('coach');
}

/**
 * Handle OAuth callback - exchange code for tokens, store them, then clean URL
 * Requirements: 1.4
 */
export async function handleOAuthCallback(): Promise<CognitoTokens | null> {
  const code = extractAuthCodeFromUrl();
  
  if (code) {
    const tokens = await exchangeCodeForTokens(code);
    
    if (tokens) {
      storeTokens(tokens);
      
      // Clean up URL (remove code parameter)
      window.history.replaceState(null, '', window.location.pathname);
      
      return tokens;
    }
  }
  
  return null;
}

/**
 * Change user password
 * Requirements: Phase 2 - User Self-Service Password Change
 * 
 * Uses AWS Amplify with proper authentication session.
 * Now works because we have the aws.cognito.signin.user.admin scope!
 * 
 * @param oldPassword - Current password
 * @param newPassword - New password (must meet Cognito requirements)
 * @throws Error if password change fails
 */
export async function changePassword(
  oldPassword: string,
  newPassword: string
): Promise<void> {
  try {
    console.log('[Auth] Attempting password change with Amplify');
    
    // Get stored tokens
    const tokens = getStoredTokens();
    if (!tokens) {
      throw new Error('Not authenticated. Please log in again.');
    }
    
    // Import Amplify auth functions
    const { fetchAuthSession, updatePassword } = await import('aws-amplify/auth');
    
    // Set the auth session in Amplify
    // This is a workaround since we manage tokens manually
    // Amplify needs to know about the session
    try {
      await fetchAuthSession({ forceRefresh: false });
    } catch (e) {
      // Session might not be set, that's okay
      console.log('[Auth] Amplify session not found, will use Cognito API directly');
    }
    
    // Try using Amplify first (preferred method)
    try {
      await updatePassword({ oldPassword, newPassword });
      console.log('[Auth] Password changed successfully via Amplify');
      return;
    } catch (amplifyError: any) {
      console.log('[Auth] Amplify method failed, trying direct Cognito API:', amplifyError.message);
      
      // Fall back to direct Cognito API call
      const response = await fetch('https://cognito-idp.us-east-2.amazonaws.com/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-amz-json-1.1',
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.ChangePassword',
        },
        body: JSON.stringify({
          AccessToken: tokens.accessToken,
          PreviousPassword: oldPassword,
          ProposedPassword: newPassword,
        }),
      });
      
      if (!response.ok) {
        const error = await response.json();
        console.error('[Auth] Password change failed:', error);
        
        // Handle specific error types
        if (error.__type === 'NotAuthorizedException') {
          throw new Error('Current password is incorrect');
        } else if (error.__type === 'InvalidPasswordException') {
          throw new Error('New password does not meet requirements');
        } else if (error.__type === 'LimitExceededException') {
          throw new Error('Too many attempts. Please try again later');
        } else if (error.__type === 'InvalidParameterException') {
          throw new Error('Invalid password format');
        } else {
          throw new Error(error.message || 'Failed to change password');
        }
      }
      
      console.log('[Auth] Password changed successfully via Cognito API');
    }
  } catch (error: any) {
    console.error('[Auth] Password change error:', error);
    throw error;
  }
}

/**
 * Initiate forgot password flow
 * Sends a password reset code to the user's email
 * 
 * @param username - User's email address
 * @throws Error if forgot password initiation fails
 */
export async function forgotPassword(username: string): Promise<void> {
  try {
    console.log('[Auth] Initiating forgot password flow for:', username);
    
    // Import Amplify auth functions
    const { resetPassword } = await import('aws-amplify/auth');
    
    // Initiate forgot password flow
    await resetPassword({ username });
    
    console.log('[Auth] Password reset code sent successfully');
  } catch (error: any) {
    console.error('[Auth] Forgot password error:', error);
    
    // Handle specific error types
    if (error.name === 'UserNotFoundException') {
      throw new Error('User not found');
    } else if (error.name === 'LimitExceededException') {
      throw new Error('Too many attempts. Please try again later');
    } else if (error.name === 'InvalidParameterException') {
      throw new Error('Invalid email address');
    } else {
      throw new Error(error.message || 'Failed to send password reset email');
    }
  }
}

/**
 * Confirm forgot password with code
 * Completes the password reset flow with the code from email
 * 
 * @param username - User's email address
 * @param code - Verification code from email
 * @param newPassword - New password
 * @throws Error if password reset confirmation fails
 */
export async function confirmForgotPassword(
  username: string,
  code: string,
  newPassword: string
): Promise<void> {
  try {
    console.log('[Auth] Confirming password reset for:', username);
    
    // Import Amplify auth functions
    const { confirmResetPassword } = await import('aws-amplify/auth');
    
    // Confirm password reset with code
    await confirmResetPassword({
      username,
      confirmationCode: code,
      newPassword,
    });
    
    console.log('[Auth] Password reset confirmed successfully');
  } catch (error: any) {
    console.error('[Auth] Confirm forgot password error:', error);
    
    // Handle specific error types
    if (error.name === 'CodeMismatchException') {
      throw new Error('Invalid verification code');
    } else if (error.name === 'ExpiredCodeException') {
      throw new Error('Verification code has expired. Please request a new one');
    } else if (error.name === 'InvalidPasswordException') {
      throw new Error('New password does not meet requirements');
    } else if (error.name === 'LimitExceededException') {
      throw new Error('Too many attempts. Please try again later');
    } else {
      throw new Error(error.message || 'Failed to reset password');
    }
  }
}
